import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { IAction, IEndState } from '../../models/workflow-builder.model';
import { ACTION_BY_TYPE } from '../../core/catalogs';

/** State rectangle: actions (Breaking / Non-breaking) on top, identity + backend colour swatch at the bottom. */
@Component({
  selector: 'irembogov-iwb-state-node',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="box"
      [id]="'box-' + (nodeKey || code)"
      [class.drop-target]="dropHover"
      [class.draggable]="draggable"
      [class.dead-end]="isDeadEnd"
      [style.borderTop]="'3px solid ' + (isDeadEnd ? '#d92d20' : colour)"
      (click)="onBoxClick($event)"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave()"
      (drop)="onDrop($event)">
      <div class="box-top" *ngIf="endState">
        <div class="lbl">
          <button
            class="mini amid"
            (click)="addAction.emit(); $event.stopPropagation()">
            ＋ action
          </button>
        </div>
        <ng-container *ngIf="required.length">
          <div class="grplbl">Breaking</div>
          <div class="acts">
            <span
              class="achip req"
              *ngFor="let a of required"
              draggable="true"
              title="Click to edit · drag onto another state to copy · right-click to copy/paste"
              (click)="onChipClick(a, $event)"
              (contextmenu)="onChipContext(a, $event)"
              (dragstart)="onChipDragStart(a, $event)"
              (dragend)="onChipDragEnd()">
              <span class="ci" [ngClass]="meta(a).cls"
                ><i class="fa-solid" [ngClass]="meta(a).icon"></i></span
              >{{ meta(a).plain }}
            </span>
          </div>
        </ng-container>
        <div class="hr" *ngIf="required.length && optional.length"></div>
        <ng-container *ngIf="optional.length">
          <div class="grplbl">Non-breaking</div>
          <div class="acts">
            <span
              class="achip"
              *ngFor="let a of optional"
              draggable="true"
              title="Click to edit · drag onto another state to copy · right-click to copy/paste"
              (click)="onChipClick(a, $event)"
              (contextmenu)="onChipContext(a, $event)"
              (dragstart)="onChipDragStart(a, $event)"
              (dragend)="onChipDragEnd()">
              <span class="ci" [ngClass]="meta(a).cls"
                ><i class="fa-solid" [ngClass]="meta(a).icon"></i></span
              >{{ meta(a).plain }}
            </span>
          </div>
        </ng-container>
        <div class="acts" *ngIf="!required.length && !optional.length">
          <span style="font-size:10px;color:#98a2b3">no actions yet</span>
        </div>
        <div class="nextev" *ngIf="endState?.nextEvent">
          → system then runs ⚡ {{ endState?.nextEvent }}
        </div>
      </div>
      <div class="box-bottom" [attr.data-cfg]="endState ? '' : null">
        <div class="iwb-swatch" [style.background]="colour"></div>
        <div style="flex:1">
          <div class="stitle">{{ name }}</div>
          <div class="scode2">{{ code }}</div>
        </div>
        <span class="endtag" *ngIf="isTerminal" [style.color]="colour"
          >end</span
        >
        <span class="endtag dead" *ngIf="isDeadEnd">dead end</span>
      </div>
    </div>
  `,
})
export class StateNodeComponent {
  @Input() code = '';
  @Input() nodeKey = '';
  @Input() name = '';
  @Input() colour = '#98a2b3';
  @Input() endState: IEndState | null = null;
  @Input() isTerminal = false;
  @Input() isDeadEnd = false;
  @Input() occurrenceIndex = 0;
  @Input() occurrenceCount = 0;
  @Input() pathTitle = '';
  /** In free mode the whole box is pointer-draggable for repositioning (handled by the container). */
  @Input() draggable = false;
  @Output() configure = new EventEmitter<void>();
  /** Click a single action chip → edit just that action (not the whole state). */
  @Output() configureAction = new EventEmitter<IAction>();
  /** Right-click a single action chip → copy/paste that action (context menu). */
  @Output() chipContext = new EventEmitter<{
    action: IAction;
    event: MouseEvent;
  }>();
  @Output() addAction = new EventEmitter<void>();
  /** Fired when the user starts dragging one of this state's action chips. */
  @Output() chipDragStart = new EventEmitter<IAction>();
  @Output() chipDragEnd = new EventEmitter<void>();
  /** Fired when an action chip is dropped onto THIS state's box (duplicate target). */
  @Output() chipDropped = new EventEmitter<void>();

  dropHover = false;

  get required(): IAction[] {
    return this.endState ? this.endState.actions.filter(a => a.required) : [];
  }
  get optional(): IAction[] {
    return this.endState ? this.endState.actions.filter(a => !a.required) : [];
  }
  meta(a: IAction) {
    return (
      ACTION_BY_TYPE[a.actionType] || {
        type: a.actionType,
        plain: a.actionType,
        icon: 'fa-gear',
        cls: 'sys',
      }
    );
  }

  /** Click anywhere on the box (except action chips / buttons) opens the STATE config. */
  onBoxClick(e: MouseEvent): void {
    if ((e.target as HTMLElement).closest('button, .addbtn, .mini, .achip'))
      return;
    if (!this.endState) return;
    this.configure.emit();
  }
  /** Click a single action chip → edit just that action. */
  onChipClick(a: IAction, e: MouseEvent): void {
    e.stopPropagation();
    this.configureAction.emit(a);
  }

  /** Right-click a single action chip → surface the copy/paste context menu. */
  onChipContext(a: IAction, e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this.chipContext.emit({ action: a, event: e });
  }

  // ── action chip drag-to-duplicate ──
  onChipDragStart(a: IAction, e: DragEvent): void {
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('text/plain', a.actionType);
    }
    this.chipDragStart.emit(a);
  }
  onChipDragEnd(): void {
    this.chipDragEnd.emit();
  }
  onDragOver(e: DragEvent): void {
    if (!this.endState) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    this.dropHover = true;
  }
  onDragLeave(): void {
    this.dropHover = false;
  }
  onDrop(e: DragEvent): void {
    if (!this.endState) return;
    e.preventDefault();
    this.dropHover = false;
    this.chipDropped.emit();
  }
}
